import { produce } from 'immer';
import _ from 'lodash';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  hasValidationErrors,
  setValidationErrors,
  showErrorToast,
} from '../../../../../../common/global/helpers';
import useMount from '../../../../../../common/hooks/useMount';
import Button from '../../../../../../common/widgets/Button';
import DatePicker from '../../../../../../common/widgets/DatePicker';
import Dropdown from '../../../../../../common/widgets/Dropdown';
import Input from '../../../../../../common/widgets/Input';
import Loader from '../../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../../common/widgets/LoadingSpinner';
import { toUtcStartOfDay } from '../../../../helpers';
import {
  AddEmployeeData,
  AddressItem,
  CustomFieldModel,
  GroupItemModel,
} from '../../../../models/EmployeeDetailsModel';
import { ErrorState, ErrorType } from '../../../../models/Errors';
import { resources } from '../../../../resources/resources';
import { employeeAdminService } from '../../../../services/employee-admin';
import SaveSuccessDialog from './Dialogs/SaveSuccessDialog';

export interface AddEmployeeState {
  employee: AddEmployeeData;
  customFieldsValues: { [key: number]: string };
  groups: GroupItemModel[];
  addresses: AddressItem[];
  showSaveSuccessDialog: boolean;
  errorType?: ErrorType;
  isSaving: boolean;
}

export default function AddEmployeePage() {
  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  const [state, setState] = useState<AddEmployeeState>({
    employee: {} as AddEmployeeData,
    customFieldsValues: {},
  } as AddEmployeeState);
  const [errors, setErrors] = useState<ErrorState>({});

  const selectedGroup = useMemo(
    () =>
      state.groups?.find(
        group => group.id === state.employee?.customerGroupId,
      ) ?? null,
    [state.groups, state.employee?.customerGroupId],
  );

  const selectedAddress = useMemo(
    () =>
      state.addresses?.find(
        address => address.addressId === state.employee?.addressId,
      ) ?? null,
    [state.addresses, state.employee?.addressId],
  );

  const loadGroups = async (abortController?: AbortController) => {
    const response = await employeeAdminService.getGroups({
      signal: abortController?.signal,
    });
    if (!response.hasErrors) {
      setState(
        produce(d => {
          d.groups = response.data;
        }),
      );
    } else {
      setState(
        produce(d => {
          d.errorType = response.errorType;
        }),
      );
    }
  };

  const loadAddresses = async (abortController?: AbortController) => {
    const response = await employeeAdminService.getAddresses({
      signal: abortController?.signal,
    });
    if (!response.hasErrors) {
      setState(
        produce(d => {
          d.addresses = response.data;
        }),
      );
    } else {
      setState(
        produce(d => {
          d.errorType = response.errorType;
        }),
      );
    }
  };

  const clearErrors = (name: string) => {
    setErrors(
      produce(draft => {
        if (draft) {
          draft[name] = [];
        }
      }),
    );
  };

  const toCustomField = (cf: CustomFieldModel) => {
    const propName = _.camelCase(cf.label);
    const hasErrors = hasValidationErrors(errors, propName);
    return (
      <div className="col col-6 col-xxs-12" key={cf.fieldId}>
        <Input
          label={cf.label}
          value={state.customFieldsValues[cf.fieldId]}
          onChange={e => {
            setState(
              produce(d => {
                d.customFieldsValues[cf.fieldId] = e.value;
              }),
            );
          }}
          mandatory={cf.isRequired}
          errors={hasErrors ? errors[propName] : []}
        />
      </div>
    );
  };

  const handleSave = async () => {
    if (!state.employee) return;
    setState(
      produce(d => {
        d.isSaving = true;
      }),
    );
    setErrors({});
    const customFieldsJson = JSON.stringify(state.customFieldsValues);
    const data: AddEmployeeData = {
      ...state.employee,
      employmentDate: state.employee.employmentDate
        ? toUtcStartOfDay(state.employee.employmentDate)
        : state.employee.employmentDate,
      liquidationDate: state.employee.liquidationDate
        ? toUtcStartOfDay(state.employee.liquidationDate)
        : state.employee.liquidationDate,
      customFieldJson: customFieldsJson ?? '',
    };

    const response = await employeeAdminService.addEmployee(data);
    if (!response.hasErrors) {
      setState(
        produce(d => {
          d.showSaveSuccessDialog = true;
        }),
      );
    } else if (response.errorType === ErrorType.ValidationError) {
      setValidationErrors(response, setErrors);
    } else {
      showErrorToast(
        translate(resources.admin.employees.details.general.errorAddEmployee),
      );
    }
    setState(
      produce(d => {
        d.isSaving = false;
      }),
    );
  };

  const handleCancel = () => {
    navigate('/admin/employee-management');
  };

  useMount((abortController?: AbortController) => {
    loadGroups(abortController);
    loadAddresses(abortController);
  });

  return (
    <div className="panel padding-xl employee-panel">
      <div className="btn-panel-back-wrapper">
        <button
          className="btn btn-panel-back"
          onClick={() => navigate('/admin/employee-management')}
        >
          <span className="btn-text">
            {translate(resources.admin.employees.details.crumbText)}
          </span>
        </button>
      </div>
      <h1>{translate(resources.admin.employees.add.title)}</h1>
      <Loader
        loading={!state.addresses || !state.groups}
        loader={<LoadingSpinner />}
      >
        <div className="admin-form">
          <div className="row">
            <div className="col col-6 col-xxs-12">
              <Dropdown
                data={state.groups ?? []}
                dataItemKey="id"
                textField="name"
                label={translate(
                  resources.admin.employees.details.general.group,
                )}
                value={selectedGroup}
                onChange={e => {
                  const group = e.value as GroupItemModel;
                  setState(
                    produce(d => {
                      d.employee.customerGroupId = group.id;
                      d.customFieldsValues = (group.customFields ?? []).reduce(
                        (
                          dic: { [key: number]: string },
                          next: CustomFieldModel,
                        ) => {
                          dic[next.fieldId] = '';
                          return dic;
                        },
                        {},
                      );
                    }),
                  );
                }}
                mandatory={true}
                errors={errors?.customerGroupId}
                clearErrors={() => clearErrors('customerGroupId')}
                controlClassName="admin-dropdown"
                popupClassName="admin-dropdown-list"
              />
            </div>
            <div className="col col-6 col-xxs-12">
              <Input
                value={state.employee?.firstName}
                label={translate(
                  resources.admin.employees.details.general.firstName,
                )}
                onChange={e =>
                  setState(
                    produce(d => {
                      if (d.employee) d.employee.firstName = e.value.toString();
                    }),
                  )
                }
                mandatory={true}
                errors={errors?.firstName}
                clearErrors={() => clearErrors('firstName')}
              />
            </div>
          </div>

          <div className="row">
            <div className="col col-6 col-xxs-12">
              <Input
                value={state.employee?.lastName}
                label={translate(
                  resources.admin.employees.details.general.lastName,
                )}
                onChange={e =>
                  setState(
                    produce(d => {
                      if (d.employee) d.employee.lastName = e.value.toString();
                    }),
                  )
                }
                mandatory={true}
                errors={errors?.lastName}
                clearErrors={() => clearErrors('lastName')}
              />
            </div>
            <div className="col col-6 col-xxs-12">
              <Input
                value={state.employee?.email}
                label={translate(
                  resources.admin.employees.details.general.email,
                )}
                onChange={e =>
                  setState(
                    produce(d => {
                      if (d.employee) d.employee.email = e.value.toString();
                    }),
                  )
                }
                mandatory={true}
                errors={errors?.email}
                clearErrors={() => clearErrors('email')}
              />
            </div>

            <div className="col col-6 col-xxs-12">
              <Input
                value={state.employee?.phoneNumber}
                label={translate(
                  resources.admin.employees.details.general.phoneNumber,
                )}
                onChange={e =>
                  setState(
                    produce(d => {
                      if (d.employee)
                        d.employee.phoneNumber = e.value.toString();
                    }),
                  )
                }
                errors={errors?.phoneNumber}
                clearErrors={() => clearErrors('phoneNumber')}
              />
            </div>

            <div className="col col-6 col-xxs-12">
              <Input
                value={state.employee?.cnp}
                label={translate(resources.admin.employees.details.general.cnp)}
                onChange={e =>
                  setState(
                    produce(d => {
                      if (d.employee) d.employee.cnp = e.value.toString();
                    }),
                  )
                }
                mandatory={selectedGroup ? selectedGroup.showCnpField : true}
                errors={errors?.cnp}
                clearErrors={() => clearErrors('cnp')}
              />
            </div>

            {!!selectedGroup?.customFields?.length &&
              selectedGroup.customFields
                .filter(cf => cf.fieldId !== 8)
                .map((cf: CustomFieldModel) => toCustomField(cf))}

            <div className="col col-6 col-xxs-12">
              <Input
                value={state.employee?.eid}
                label={translate(resources.admin.employees.details.general.eid)}
                onChange={e =>
                  setState(
                    produce(d => {
                      if (d.employee) d.employee.eid = e.value;
                    }),
                  )
                }
                mandatory={true}
                errors={errors?.eid}
                clearErrors={() => clearErrors('eid')}
              />
            </div>

            <div className="col col-6 col-xxs-12">
              <Dropdown
                data={state.addresses}
                dataItemKey="itemKey"
                textField="address"
                label={translate(
                  resources.admin.employees.details.general.address,
                )}
                value={selectedAddress}
                onChange={e =>
                  setState(
                    produce(d => {
                      d.employee.addressId = (e.value as AddressItem).addressId;
                    }),
                  )
                }
                mandatory={true}
                errors={errors?.addressId}
                clearErrors={() => clearErrors('addressId')}
                controlClassName="admin-dropdown"
                popupClassName="admin-dropdown-list"
              />
            </div>

            <div className="col col-6 col-xxs-12">
              <DatePicker
                value={state.employee?.employmentDate ?? undefined}
                label={translate(
                  resources.admin.employees.details.general.employmentDate,
                )}
                onChange={value =>
                  setState(
                    produce(d => {
                      if (d.employee) d.employee.employmentDate = value;
                    }),
                  )
                }
                mandatory={true}
                showClearButton
                errors={errors?.employmentDate}
                clearErrors={() => clearErrors('employmentDate')}
              />
            </div>

            <div className="col col-6 col-xxs-12">
              <DatePicker
                label={translate(
                  resources.admin.employees.details.general.liquidationDate,
                )}
                value={state.employee?.liquidationDate ?? undefined}
                onChange={value => {
                  setState(
                    produce(d => {
                      if (d.employee)
                        d.employee.liquidationDate = value ?? null;
                    }),
                  );
                }}
                showClearButton
                errors={errors?.liquidationDate}
                clearErrors={() => clearErrors('liquidationDate')}
              />
            </div>

            <div className="col col-6 col-xxs-12">
              <Input
                value={state.employee?.costCenter}
                label={translate(
                  resources.admin.employees.details.general.costCenter,
                )}
                onChange={e =>
                  setState(
                    produce(d => {
                      if (d.employee)
                        d.employee.costCenter = e.value.toString();
                    }),
                  )
                }
                errors={errors?.costCenter}
                clearErrors={() => clearErrors('costCenter')}
              />
            </div>

            <div className="col col-6 col-xxs-12">
              <Input
                value={state.employee?.department}
                label={translate(
                  resources.admin.employees.details.general.department,
                )}
                onChange={e =>
                  setState(
                    produce(d => {
                      if (d.employee) d.employee.department = e.value;
                    }),
                  )
                }
              />
            </div>
          </div>

          <div className="form-actions">
            <Button
              className="btn btn-solid"
              onClick={handleSave}
              loading={state.isSaving}
            >
              <span className="btn-text">
                {translate(resources.admin.employees.details.general.save)}
              </span>
            </Button>
            <Button className="btn btn-outline" onClick={handleCancel}>
              <span className="btn-text">
                {translate(resources.admin.employees.details.general.cancel)}
              </span>
            </Button>
          </div>
        </div>
      </Loader>
      {state.showSaveSuccessDialog && (
        <SaveSuccessDialog
          close={() => {
            setState(
              produce(d => {
                d.showSaveSuccessDialog = false;
              }),
            );
            navigate('/admin/employee-management');
          }}
        />
      )}
    </div>
  );
}
